<template>
  <modal-vue
    :modal-active="modelValue"
    max-width="max-w-[600px]"
    @close="$emit('update:modelValue', false)"
  >
    <template #header>
      <h2 class="text-lg font-bold text-secondary50">{{ title }}</h2>
    </template>

    <p class="text-base text-secondary50 leading-relaxed">{{ message }}</p>

    <ConsecutiveHoursBlockAlert
      v-if="variant === 'approve'"
      :violations="consecutiveHoursViolations ?? []"
      show-block-badge
      class="mt-4"
    />

    <div
      v-if="hasScheduleConflicts"
      data-testid="schedule-conflict-alert"
      class="flex flex-col gap-2 rounded-lg p-3 mt-4 bg-[#FEF9E7] border border-warning text-left"
    >
      <div class="flex flex-wrap items-center gap-2">
        <p class="text-sm text-[#16161d] leading-[18px]">
          <span class="font-bold">Conflito de agenda:</span> Dr(a).
          {{ scheduleConflicts?.doctorName }} já está alocado(a) em
          {{ scheduleConflicts?.conflicts.length }} plantão(ões) que se sobrepõe(m) a este convite:
        </p>
        <LabelBadge label="REQUER ATENÇÃO" color="yellow" size="small" :icon="null" />
      </div>
      <ul class="flex flex-col gap-1">
        <li
          v-for="(conflict, index) in scheduleConflicts?.conflicts"
          :key="index"
          class="text-sm text-[#16161d] leading-[18px]"
        >
          • {{ formatConflictDate(conflict.date) }} · {{ conflict.startHour }}—{{
            conflict.endHour
          }}
          · {{ conflict.healthUnitName }} — {{ conflict.scheduleName }}
        </li>
      </ul>
      <p class="text-sm text-[#16161d] leading-[18px] font-medium">
        Este conflito não impede a aprovação — você pode aprovar mesmo assim.
      </p>
    </div>

    <div
      v-if="variant === 'approve'"
      data-testid="approval-reminder-callout"
      class="flex items-start gap-2 rounded-lg p-3 mt-4 bg-[#edf5ff] border border-[#0f62fe]"
    >
      <v-icon :icon="mdiInformationOutline" size="18" color="#0f62fe" class="shrink-0 mt-0.5" />
      <p class="text-sm text-[#16161d] leading-[18px] text-left">
        Ao aprovar, você confirma a autorização do profissional para este plantão e garante a
        atualização no sistema ou planilha da contratante.
      </p>
    </div>

    <div class="flex items-center gap-6 mt-6">
      <OutlineButton
        v-if="variant === 'approve'"
        text="Cancelar"
        width="48%"
        height="40px"
        :disabled="loading"
        @click="$emit('update:modelValue', false)"
      />
      <FilledButton
        v-if="variant === 'approve'"
        :text="confirmButtonText"
        width="48%"
        height="40px"
        :disabled="loading || hasConsecutiveHoursViolations"
        class="bg-primary30"
        @click="$emit('confirm')"
      />

      <FilledButton
        v-if="variant === 'decline'"
        text="Cancelar"
        width="48%"
        height="40px"
        :disabled="loading"
        class="bg-primary30 border-2 border-solid"
        @click="$emit('update:modelValue', false)"
      />
      <v-btn
        v-if="variant === 'decline'"
        variant="outlined"
        color="error"
        class="flex-1 normal-case border-2 font-bold"
        width="48%"
        height="40"
        :disabled="loading"
        @click="$emit('confirm')"
      >
        Confirmar
      </v-btn>
    </div>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { format, parseISO } from 'date-fns'
import { mdiInformationOutline } from '@mdi/js'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import ConsecutiveHoursBlockAlert from '@/components/designSystem/Alerts/ConsecutiveHoursBlockAlert.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { ScheduleConflictsSummary } from '@/Network/Types/Responses/HandoverInviteResponses'
import type { ConsecutiveHoursViolation } from '@/Network/Types/Responses/ConsecutiveHoursResponses'

const props = defineProps<{
  modelValue: boolean
  title: string
  message: string
  variant: 'approve' | 'decline'
  loading: boolean
  scheduleConflicts?: ScheduleConflictsSummary | null
  consecutiveHoursViolations?: ConsecutiveHoursViolation[] | null
}>()

defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
}>()

const hasScheduleConflicts = computed(
  () =>
    props.variant === 'approve' &&
    !!props.scheduleConflicts &&
    props.scheduleConflicts.conflicts.length > 0
)

// Bloqueio duro: diferente do conflito de agenda, esta regra não tem "mesmo assim".
const hasConsecutiveHoursViolations = computed(
  () => props.variant === 'approve' && (props.consecutiveHoursViolations?.length ?? 0) > 0
)

const confirmButtonText = computed(() =>
  hasScheduleConflicts.value && !hasConsecutiveHoursViolations.value
    ? 'Aprovar mesmo assim'
    : 'Aprovar'
)

const formatConflictDate = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy')
  } catch {
    return dateStr
  }
}
</script>

<style scoped>
:deep(header) {
  padding-bottom: 0 !important;
}
</style>
